import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { GraduationCap, Calculator, Award, Info, Scale, HelpCircle, BookOpen, Menu, X } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import Home from "./pages/Home";
import CalculatorPage from "./pages/Calculator";
import Scholarships from "./pages/Scholarships";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import Compare from "./pages/Compare";
import FAQ from "./pages/FAQ";
import Resources from "./pages/Resources";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navLinks = [
    { to: "/calculator", icon: Calculator, label: "Calculator" },
    { to: "/compare", icon: Scale, label: "Compare" },
    { to: "/scholarships", icon: Award, label: "Scholarships" },
    { to: "/resources", icon: BookOpen, label: "Resources" },
    { to: "/faq", icon: HelpCircle, label: "FAQ" },
  ];
  
  return (
    <nav className="border-b border-slate-800 bg-slate-950/95 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-blue-400 transition-colors">
            <GraduationCap className="w-6 h-6 text-blue-400" />
            <span className="font-bold text-lg">CollegeTrue</span>
          </Link>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium">
                <link.icon className="w-4 h-4" />
                <span>{link.label}</span>
              </Link>
            ))}
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-slate-400 hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-800">
            {navLinks.map(link => (
              <Link 
                key={link.to} 
                to={link.to} 
                className="flex items-center gap-3 py-3 text-slate-400 hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <link.icon className="w-5 h-5" />
                <span>{link.label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="border-t border-slate-200 bg-slate-900 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="w-5 h-5 text-blue-400" />
                <span className="font-bold text-white">CollegeTrue</span>
              </div>
              <p className="text-slate-400 text-sm">
                Making college affordability transparent through data-driven insights.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-3">Tools</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/calculator" className="text-slate-400 hover:text-blue-400 transition-colors">Cost Calculator</Link></li>
                <li><Link to="/compare" className="text-slate-400 hover:text-blue-400 transition-colors">College Comparison</Link></li>
                <li><Link to="/scholarships" className="text-slate-400 hover:text-blue-400 transition-colors">Scholarship Matcher</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-3">Learn</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/faq" className="text-slate-400 hover:text-blue-400 transition-colors">FAQ</Link></li>
                <li><Link to="/resources" className="text-slate-400 hover:text-blue-400 transition-colors">Resources & Tips</Link></li>
                <li><Link to="/about" className="text-slate-400 hover:text-blue-400 transition-colors">About Us</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-blue-400 transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-3">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-slate-400 hover:text-blue-400 transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-blue-400 transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-8 text-center">
            <p className="text-slate-500 text-xs mb-2">
              © {new Date().getFullYear()} CollegeTrue. All rights reserved.
            </p>
            <p className="text-slate-400 text-xs max-w-3xl mx-auto">
              Data sources: Bureau of Labor Statistics, NACE, College Board, and official state financial aid websites. 
              This tool provides estimates and should not be considered financial advice.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/scholarships" element={<Scholarships />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        <Footer />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;